/* eslint-disable react/prop-types */
import {
	useState,
	useEffect,
	useRef,
	useCallback,
	forwardRef,
	useImperativeHandle
} from "react";
import { UserCircle, Hash } from "@phosphor-icons/react";
import cmsClient from "src/services/cmsClient";
import { SEARCH_MIN_CHARS } from "src/constants";

interface Suggestion {
	id: string;
	name: string;
	subtitle?: string;
}

interface TokenMapping {
	type: "@" | "#";
	id: string;
}

interface MentionHashtagInputProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	autoFocus?: boolean;
	onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	minHeight?: string;
	disabled?: boolean;
	rows?: number;
	ariaLabel?: string;
}

/** Convert markdown body → display text + token map */
const parseMarkdown = (markdown: string) => {
	const map = new Map<string, TokenMapping>();
	const display = markdown.replace(
		/#\[(.*?)\]\((.*?)\)|@\[(.*?)\]\((.*?)\)/g,
		(_, hName, hId, mName, mId) => {
			if (hName !== undefined) {
				map.set(hName, { type: "#", id: hId });
				return `#${hName}`;
			}
			map.set(mName, { type: "@", id: mId });
			return `@${mName}`;
		}
	);
	return { display, map };
};

/** Convert display text → markdown using token map */
const toMarkdown = (
	display: string,
	map: Map<string, TokenMapping>
): string => {
	let result = display;
	// Sort by name length descending to avoid substring replacement issues
	const entries = [...map.entries()].sort((a, b) => b[0].length - a[0].length);
	for (const [name, { type, id }] of entries) {
		const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const trigger = type === "#" ? "#" : "@";
		const pattern = new RegExp(`(^|\\s)${trigger}${escapedName}(?!\\w)`, "g");
		const replacement =
			type === "#" ? `#[${name}](${id})` : `@[${name}](${id})`;
		result = result.replace(
			pattern,
			(_, boundary) => `${boundary}${replacement}`
		);
	}
	return result;
};

const MentionHashtagInput = forwardRef<
	HTMLTextAreaElement,
	MentionHashtagInputProps
>(
	(
		{
			value,
			onChange,
			placeholder,
			className,
			autoFocus,
			onKeyDown,
			minHeight = "120px",
			disabled = false,
			rows,
			ariaLabel
		},
		ref
	) => {
		const textareaRef = useRef<HTMLTextAreaElement>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const tokenMapRef = useRef<Map<string, TokenMapping>>(new Map());
		const isInternalChangeRef = useRef(false);

		useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

		const [trigger, setTrigger] = useState<"@" | "#" | null>(null);
		const [query, setQuery] = useState("");
		const [debouncedQuery, setDebouncedQuery] = useState("");
		const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
		const [triggerStart, setTriggerStart] = useState(0);
		const [cursorEnd, setCursorEnd] = useState(0);
		const [selectedIndex, setSelectedIndex] = useState(0);
		const [isLoading, setIsLoading] = useState(false);

		// Display text: what the user sees (no markdown syntax)
		const [displayText, setDisplayText] = useState(() => {
			const { display, map } = parseMarkdown(value);
			tokenMapRef.current = map;
			return display;
		});

		// Sync from external value changes (e.g. edit dialog pre-populating)
		useEffect(() => {
			if (isInternalChangeRef.current) {
				isInternalChangeRef.current = false;
				return;
			}
			const { display, map } = parseMarkdown(value);
			setDisplayText(display);
			tokenMapRef.current = map;
		}, [value]);

		// Debounce the query
		useEffect(() => {
			const timer = setTimeout(() => {
				setDebouncedQuery(query);
			}, 300);
			return () => clearTimeout(timer);
		}, [query]);

		// Fetch suggestions
		useEffect(() => {
			const minChars = trigger === "#" ? SEARCH_MIN_CHARS : 1;
			if (!debouncedQuery || debouncedQuery.length < minChars || !trigger) {
				setSuggestions([]);
				setIsLoading(false);
				return;
			}

			let cancelled = false;
			setIsLoading(true);

			if (trigger === "@") {
				cmsClient
					.get("/socialprofile/search/", {
						params: { search_term: debouncedQuery }
					})
					.then((res) => {
						if (cancelled) return;
						setSuggestions(
							(res.data || []).map(
								(u: {
									username: string;
									full_name?: string;
									role?: string;
								}) => ({
									id: u.username,
									name: u.full_name || u.username,
									subtitle: u.role ?? ""
								})
							)
						);
						setIsLoading(false);
					})
					.catch(() => {
						if (!cancelled) {
							setSuggestions([]);
							setIsLoading(false);
						}
					});
			}

			if (trigger === "#") {
				cmsClient
					.get("/hashtag/", {
						params: { search: debouncedQuery }
					})
					.then((res) => {
						if (cancelled) return;
						setSuggestions(
							(res.data?.results || []).map(
								(h: { uuid?: string; name: string; description?: string }) => ({
									id: h.uuid || h.name,
									name: h.name,
									subtitle: h.description ?? ""
								})
							)
						);
						setIsLoading(false);
					})
					.catch(() => {
						if (!cancelled) {
							setSuggestions([]);
							setIsLoading(false);
						}
					});
			}

			return () => {
				cancelled = true;
			};
		}, [debouncedQuery, trigger]);

		// Reset selected index when suggestions change
		useEffect(() => {
			setSelectedIndex(0);
		}, [suggestions]);

		// Close dropdown on click outside
		useEffect(() => {
			const handleClickOutside = (e: MouseEvent) => {
				if (
					containerRef.current &&
					!containerRef.current.contains(e.target as Node)
				) {
					setTrigger(null);
					setQuery("");
					setSuggestions([]);
				}
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}, []);

		const detectTrigger = useCallback((text: string, cursorPos: number) => {
			// Look backwards from cursor for @ or #
			const textBeforeCursor = text.substring(0, cursorPos);

			// Find the last @ or # that isn't preceded by a word character
			let lastTriggerPos = -1;
			let lastTriggerChar: "@" | "#" | null = null;

			for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
				const char = textBeforeCursor[i];

				// If we hit a space or newline before finding a trigger, stop
				if (char === " " || char === "\n" || char === "\r") {
					break;
				}

				if (char === "@" || char === "#") {
					// Check the character before the trigger
					const charBefore = i > 0 ? textBeforeCursor[i - 1] : undefined;
					// Valid if at start of text, after a space, or after a newline
					if (
						!charBefore ||
						charBefore === " " ||
						charBefore === "\n" ||
						charBefore === "\r"
					) {
						lastTriggerPos = i;
						lastTriggerChar = char as "@" | "#";
					}
					break;
				}
			}

			if (lastTriggerChar && lastTriggerPos >= 0) {
				const queryText = textBeforeCursor.substring(lastTriggerPos + 1);
				if (queryText.length >= 1) {
					setTrigger(lastTriggerChar);
					setQuery(queryText);
					setTriggerStart(lastTriggerPos);
					setCursorEnd(cursorPos);
					return;
				} else if (queryText.length === 0) {
					// Just typed the trigger char, wait for more input
					setTrigger(lastTriggerChar);
					setQuery("");
					setTriggerStart(lastTriggerPos);
					setCursorEnd(cursorPos);
					setSuggestions([]);
					return;
				}
			}

			// No active trigger
			setTrigger(null);
			setQuery("");
			setSuggestions([]);
		}, []);

		const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newDisplay = e.target.value;
			setDisplayText(newDisplay);

			// Clean up token map — remove tokens that no longer appear in text
			for (const [name, { type }] of tokenMapRef.current) {
				const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
				const trigger = type === "#" ? "#" : "@";
				const boundaryPattern = new RegExp(
					`(^|\\s)${trigger}${escapedName}(?!\\w)`
				);
				if (!boundaryPattern.test(newDisplay)) {
					tokenMapRef.current.delete(name);
				}
			}

			const markdown = toMarkdown(newDisplay, tokenMapRef.current);
			isInternalChangeRef.current = true;
			onChange(markdown);

			const cursorPos = e.target.selectionStart ?? 0;
			detectTrigger(newDisplay, cursorPos);
		};

		const handleSelect = useCallback(
			(suggestion: Suggestion) => {
				const before = displayText.substring(0, triggerStart);
				const after = displayText.substring(cursorEnd);

				let insertText = "";
				if (trigger === "@") {
					insertText = `@${suggestion.name} `;
					tokenMapRef.current.set(suggestion.name, {
						type: "@",
						id: suggestion.id
					});
				} else if (trigger === "#") {
					insertText = `#${suggestion.name} `;
					tokenMapRef.current.set(suggestion.name, {
						type: "#",
						id: suggestion.id
					});
				}

				const newDisplay = before + insertText + after;
				setDisplayText(newDisplay);

				const markdown = toMarkdown(newDisplay, tokenMapRef.current);
				isInternalChangeRef.current = true;
				onChange(markdown);

				setTrigger(null);
				setQuery("");
				setSuggestions([]);

				// Restore focus and set cursor position after the inserted text
				requestAnimationFrame(() => {
					if (textareaRef.current) {
						textareaRef.current.focus();
						const newCursorPos = before.length + insertText.length;
						textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
					}
				});
			},
			[displayText, triggerStart, cursorEnd, trigger, onChange]
		);

		const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (trigger && suggestions.length > 0) {
				if (e.key === "ArrowDown") {
					e.preventDefault();
					setSelectedIndex((prev) =>
						prev < suggestions.length - 1 ? prev + 1 : 0
					);
					return;
				}
				if (e.key === "ArrowUp") {
					e.preventDefault();
					setSelectedIndex((prev) =>
						prev > 0 ? prev - 1 : suggestions.length - 1
					);
					return;
				}
				if (e.key === "Enter") {
					e.preventDefault();
					if (suggestions[selectedIndex])
						handleSelect(suggestions[selectedIndex]);
					return;
				}
				if (e.key === "Tab") {
					e.preventDefault();
					if (suggestions[selectedIndex])
						handleSelect(suggestions[selectedIndex]);
					return;
				}
			}

			if (e.key === "Escape" && trigger) {
				e.preventDefault();
				setTrigger(null);
				setQuery("");
				setSuggestions([]);
				return;
			}

			// Pass through to parent handler
			onKeyDown?.(e);
		};

		// Scroll selected item into view
		useEffect(() => {
			if (dropdownRef.current && suggestions.length > 0) {
				const selected = dropdownRef.current.children[
					selectedIndex
				] as HTMLElement;
				if (selected) {
					selected.scrollIntoView({ block: "nearest" });
				}
			}
		}, [selectedIndex, suggestions.length]);

		const showDropdown = trigger !== null && suggestions.length > 0;

		return (
			<div ref={containerRef} className="relative">
				<textarea
					ref={textareaRef}
					value={displayText}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClick={() => {
						if (textareaRef.current) {
							const cursorPos = textareaRef.current.selectionStart ?? 0;
							detectTrigger(displayText, cursorPos);
						}
					}}
					placeholder={placeholder}
					className={className}
					style={{ minHeight }}
					rows={rows}
					autoFocus={autoFocus}
					disabled={disabled}
					aria-label={ariaLabel}
					role="combobox"
					aria-expanded={showDropdown}
					aria-controls={showDropdown ? "mention-hashtag-listbox" : undefined}
					aria-activedescendant={
						showDropdown && suggestions[selectedIndex]
							? `suggestion-${selectedIndex}`
							: undefined
					}
					aria-autocomplete="list"
				/>

				{showDropdown && (
					<div
						ref={dropdownRef}
						id="mention-hashtag-listbox"
						className="absolute left-0 right-0 z-10 mt-1 max-h-[200px] overflow-y-auto rounded-lg border border-border bg-white shadow-lg"
						role="listbox"
						aria-label={
							trigger === "@" ? "Mention suggestions" : "Hashtag suggestions"
						}
					>
						{suggestions.map((suggestion, index) => (
							<button
								key={suggestion.id}
								id={`suggestion-${index}`}
								type="button"
								role="option"
								aria-selected={index === selectedIndex}
								aria-label={`Select ${trigger}${suggestion.name}`}
								className={[
									"flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
									index === selectedIndex ? "bg-muted" : "hover:bg-muted/50"
								].join(" ")}
								onMouseDown={(e) => {
									e.preventDefault();
									handleSelect(suggestion);
								}}
								onMouseEnter={() => setSelectedIndex(index)}
							>
								{trigger === "@" ? (
									<UserCircle
										size={24}
										weight="duotone"
										className="shrink-0 text-muted-foreground"
									/>
								) : (
									<Hash
										size={24}
										weight="duotone"
										className="shrink-0 text-muted-foreground"
									/>
								)}
								<div className="min-w-0 flex-1">
									<p className="truncate font-medium text-foreground">
										{suggestion.name}
									</p>
									{suggestion.subtitle && (
										<p className="truncate text-[13px] text-muted-foreground">
											{suggestion.subtitle}
										</p>
									)}
								</div>
							</button>
						))}
					</div>
				)}

				{trigger !== null &&
					isLoading &&
					suggestions.length === 0 &&
					query.length >= 1 && (
						<div className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-border bg-white px-3 py-3 shadow-lg">
							<p className="text-center text-sm text-muted-foreground">
								Searching...
							</p>
						</div>
					)}
			</div>
		);
	}
);

MentionHashtagInput.displayName = "MentionHashtagInput";

export default MentionHashtagInput;
